import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { Modal, Button, Icon, Row, Col } from '@folio/stripes/components';
import { PersistedPaneset } from '@folio/stripes/smart-components';

import { MessageList, ConnectionStatus, VoiceInput } from '../';
import { useChatInterface } from '../../hooks/useSmartHelp';

import css from './SmartHelpModal.css';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onResponse: PropTypes.func,
};

const defaultProps = {
  onResponse: () => {},
};

const SmartHelpModal = ({ open, onClose, onResponse }) => {
  const intl = useIntl();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [sessionId] = useState(
    () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  );

  const { sendMessage, clearChat, isConnected, isLoading, error } =
    useChatInterface(sessionId);

  const label = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {intl.formatMessage({ id: 'ui-plugin-smart-help.modal.title' })}
      <ConnectionStatus showLabel={false} />
    </div>
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (message.trim() && !isLoading) {
      handleSendMessage(message.trim());
      setMessage('');

      // Reset textarea height
      const textarea = e.target.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e) => {
    const textarea = e.target;
    setMessage(textarea.value);

    // Auto-resize textarea
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleVoiceInput = (transcript) => {
    // Only fill the input field, don't auto-submit
    if (transcript && transcript.trim()) {
      const cleanTranscript = transcript.trim();
      setMessage(prev => prev ? prev + ' ' + cleanTranscript : cleanTranscript);
      
      // Auto-resize textarea after adding text
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
        }
      }, 0);
    }
  };

  const handleSendMessage = async (messageText) => {
    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // Use async/await for better control
    sendMessage(messageText)
      .then((result) => {
        console.log('Received response:', result);
        if (result?.success && result?.data) {
          const botMessage = {
            id: Date.now() + 1,
            text: result.data.message,
            sender: 'bot',
            timestamp: new Date(),
            sources: result.data.sources || [],
            sourceCount: result.data.sourceCount || 0,
            sessionId: result.data.sessionId,
          };

          setMessages((prev) => [...prev, botMessage]);
          onResponse(result.data);
        } else {
          const errorMessage = {
            id: Date.now() + 1,
            text: intl.formatMessage({
              id: 'ui-plugin-smart-help.error.invalidResponse',
            }),
            sender: 'bot',
            timestamp: new Date(),
            isError: true,
          };

          setMessages((prev) => [...prev, errorMessage]);
        }
      })
      .catch((error) => {
        console.error('Send message error:', error);
        const errorMessage = {
          id: Date.now() + 1,
          text: intl.formatMessage({
            id: 'ui-plugin-smart-help.error.failedToGetResponse',
          }),
          sender: 'bot',
          timestamp: new Date(),
          isError: true,
        };

        setMessages((prev) => [...prev, errorMessage]);
      });
  };

  const handleClearChat = async () => {
    try {
      await clearChat();
      setMessages([]);
    } catch (error) {
      console.error('Failed to clear chat:', error);
      // Still clear local messages even if server call fails
      setMessages([]);
    }
  };

  const renderFooter = () => (
    <div className={css.modalFooter}>
      <div className={css.footerRow}>
        <form onSubmit={handleSubmit} className={css.inputForm}>
          <div className={css.inputContainer}>
            <textarea
              value={message}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-smart-help.chat.inputPlaceholder',
              })}
              disabled={isLoading}
              rows={1}
              className={css.messageInput}
            />
            <VoiceInput
              onVoiceInput={handleVoiceInput}
              disabled={isLoading}
              language="ar-SA"
            />
            <Button
              type="submit"
              buttonStyle="primary"
              disabled={!message.trim() || isLoading}
              className={css.sendButton}
              marginBottom0
            >
              <Icon icon="arrow-up" size="small" />
            </Button>
          </div>
        </form>

        <Button
          buttonStyle="ghost"
          onClick={handleClearChat}
          disabled={isLoading || messages.length === 0}
          className={css.clearButton}
          title={intl.formatMessage({
            id: 'ui-plugin-smart-help.chat.clearChat',
          })}
        >
          <Icon icon="trash" size="small" />
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      dismissible
      label={label}
      ariaLabel={
        typeof label === 'string'
          ? label
          : intl.formatMessage({ id: 'ui-plugin-smart-help.modal.title' })
      }
      onClose={onClose}
      size="large"
      id="smart-help-modal"
      data-testid="smart-help-modal"
      contentClass={css.modalContent}
      enforceFocus={false}
      footer={renderFooter()}
    >
      <div className={css.chatContainer}>
        <MessageList messages={messages} isLoading={isLoading} />
      </div>
    </Modal>
  );
};

SmartHelpModal.propTypes = propTypes;
SmartHelpModal.defaultProps = defaultProps;

export default SmartHelpModal;
